// ---------------------------------------------------------------------------
// Builds the result the `IpawsSubmissionResultModal` shows from a FEMA submit response or a
// failed request. Shared so every caller of `useSubmitFemaAlertMutation` (the AlertCreator
// submission flow and the FEMA alert list's Update/Cancel actions) reads the same outcome the
// same way and shows it in the same modal.
// ---------------------------------------------------------------------------
import type {
  FemaEnrichedAdvisory,
  FemaOutcomeFieldFinding,
  FemaSendGateResponse,
  FemaSubmissionOutcome,
} from 'types/fema.types';
import type { FemaCancelResponse, FemaUpdateResponse } from 'types/femaMessage.types';

type StatusVariant = 'secondary' | 'success' | 'warning' | 'danger';

/** Details for a message the server prepared and signed but did not send. */
export type IpawsSendGateDetails = {
  capIdentifier: string;
  digestValue: string;
  subjectName: string;
  signedXmlFormatted: string;
};

export type IpawsSubmissionStatusMessage = {
  variant: StatusVariant;
  acceptanceMessage: string;
  text: string;
  findings?: FemaOutcomeFieldFinding[];
  /** Every FEMA status item returned with the outcome, enriched with the registry's channel/remark. */
  advisories?: FemaEnrichedAdvisory[];
  /** FEMA's Message Viewer, so the operator can look the alert up by COG once it reached IPAWS. */
  verificationUrl?: string;
  cogId?: string;
  gate?: IpawsSendGateDetails;
  /** When true, closing the result keeps the draft so the operator can fix or retry it. */
  keepDraft?: boolean;
};

export const INITIAL_SUBMISSION_MESSAGE: IpawsSubmissionStatusMessage = {
  variant: 'secondary',
  acceptanceMessage: '',
  text: '',
};

/** The body the backend returns when it refuses an alert (400: message, findings, recorded). */
type BackendErrorBody = {
  code?: string;
  message?: string;
  findings?: FemaOutcomeFieldFinding[];
};

/**
 * The error RTK hands the caller. `axiosBaseQuery` (services/_infra/rtkBaseQuery.js) puts the
 * response body under `raw` — never `data` — so a reader that only looks at `data` shows the
 * message and drops every finding (it did, until 2026-08-28). Both are read: a fetch-shaped or
 * mocked error still explains itself.
 */
type BackendErrorShape = {
  message?: string;
  data?: BackendErrorBody;
  raw?: BackendErrorBody | null;
};

/** Reads a useful message from an unknown server error without assuming its shape is valid. */
export const readBackendError = (
  error: unknown,
  fallback: string,
): { message: string; findings?: FemaOutcomeFieldFinding[] } => {
  if (!error || typeof error !== 'object') return { message: fallback };
  const candidate = error as BackendErrorShape;
  const body = candidate.raw ?? candidate.data;
  const findings = Array.isArray(body?.findings) ? body?.findings : undefined;
  return { message: body?.message ?? candidate.message ?? fallback, findings };
};

const getSubmissionResultVariant = (outcome: FemaSubmissionOutcome): StatusVariant => {
  if (outcome.outcome === 'acceptance') return 'success';
  if (
    outcome.outcome === 'advisory' ||
    outcome.outcome === 'warning' ||
    outcome.outcome === 'accepted-not-disseminated'
  )
    return 'warning';
  return 'danger';
};

/** Every outcome that carries the backend's full status-item list — undefined otherwise. */
const getOutcomeAdvisories = (
  outcome: FemaSubmissionOutcome,
): FemaEnrichedAdvisory[] | undefined => {
  if (
    outcome.outcome === 'advisory' ||
    outcome.outcome === 'warning' ||
    outcome.outcome === 'accepted-not-disseminated' ||
    outcome.outcome === 'rejection'
  )
    return outcome.advisories;
  return undefined;
};

/** Every outcome that reached IPAWS under a known COG — undefined otherwise. */
const getOutcomeVerification = (
  outcome: FemaSubmissionOutcome,
): { verificationUrl?: string; cogId?: string } | undefined => {
  if (
    outcome.outcome === 'acceptance' ||
    outcome.outcome === 'advisory' ||
    outcome.outcome === 'warning' ||
    outcome.outcome === 'accepted-not-disseminated'
  )
    return { verificationUrl: outcome.verificationUrl, cogId: outcome.cogId };
  return undefined;
};

type ProcessedOutcome = {
  valid: boolean;
  acceptanceMessage: string;
  message: string;
  findings?: FemaOutcomeFieldFinding[];
};

/** The result to show while the send gate is closed: built and signed, NOT sent. */
export const buildSendGateResultMessage = (
  body: FemaSendGateResponse,
): IpawsSubmissionStatusMessage => ({
  variant: 'warning',
  acceptanceMessage: 'Reached the FEMA send gate.',
  text:
    'Transmission is disabled until approved. The message was built, ' +
    `signed and verified but NOT sent. ${body.message}`,
  gate: {
    capIdentifier: body.capIdentifier,
    digestValue: body.digestValue,
    subjectName: body.subjectName,
    signedXmlFormatted: body.signedXmlFormatted,
  },
  keepDraft: true,
});

/** The result to show for a normal (non-gated) FEMA outcome, once `processFemaSubmissionOutcome` has run. */
export const buildOutcomeResultMessage = (
  outcome: FemaSubmissionOutcome,
  processedResult: ProcessedOutcome,
): IpawsSubmissionStatusMessage => ({
  variant: getSubmissionResultVariant(outcome),
  acceptanceMessage: processedResult.acceptanceMessage,
  text: processedResult.message,
  findings: processedResult.findings,
  advisories: getOutcomeAdvisories(outcome),
  ...getOutcomeVerification(outcome),
  keepDraft: !processedResult.valid,
});

/** The result to show when the submit request itself failed (network error or a 4xx refusal). */
export const buildSubmissionErrorMessage = (
  error: unknown,
  fallback: string,
  acceptanceMessage: string,
): IpawsSubmissionStatusMessage => {
  const { message, findings } = readBackendError(error, fallback);
  return {
    variant: 'danger',
    acceptanceMessage,
    text: message,
    findings,
    keepDraft: true,
  };
};

/**
 * The result to show after `POST /v2/fema/:id/cancel`: ONE CAP Cancel for the ONE message named
 * (card BE-28). `result.cancelled` decides the variant — it is read from the body, never the HTTP
 * status, because a refused Cancel (409) carries the same `result` shape with `cancelled: false`.
 */
export const buildCancelResultMessage = (
  response: FemaCancelResponse,
): IpawsSubmissionStatusMessage => {
  const { result } = response;
  // The reason is a sentence of its own; drop its full stop so the line does not end in two.
  const refusalReason = (result.reason ?? response.message ?? 'FEMA did not accept the Cancel')
    .trim()
    .replace(/\.$/, '');
  const reasonPart = result.reason ? ` — ${result.reason}` : '';
  const codesPart = result.codes?.length ? ` (codes ${result.codes.join(' ')})` : '';

  return {
    variant: result.cancelled ? 'success' : 'danger',
    acceptanceMessage: result.cancelled
      ? `Cancelled — ${result.capIdentifier} taken off the air.`
      : `NOT cancelled — ${refusalReason}.`,
    text: `Message ${result.messageId} (${result.capIdentifier}): ${result.action}${reasonPart}${codesPart}`,
    keepDraft: !result.cancelled,
  };
};

/**
 * The result to show after `POST /v2/fema/:id/update`. `result.accepted` decides the variant —
 * never the HTTP status, for the same reason as every other FEMA outcome here.
 */
export const buildUpdateResultMessage = (
  response: FemaUpdateResponse,
): IpawsSubmissionStatusMessage => {
  const { result } = response;

  if (result.accepted) {
    return {
      variant: 'success',
      acceptanceMessage: 'Update accepted.',
      text:
        `It supersedes message ${result.referencesMessageId}. Earlier versions of this alert ` +
        'stay active at FEMA until the alert is cancelled.',
      keepDraft: false,
    };
  }

  return {
    variant: 'danger',
    acceptanceMessage: 'Update not accepted.',
    text: result.codes.length ? `FEMA codes: ${result.codes.join(', ')}.` : response.message,
    findings: response.findings,
    keepDraft: true,
  };
};
